import React, { useCallback, useContext, useEffect, useState } from 'react';

import styled from 'styled-components';

import { APICompany, APIResponse, APIUserType } from '@cd3p/core/types/api';
import { textField } from '@cd3p/core/utils/fields';
import { Stack } from '@mui/material';
import {
  Button,
  ConfirmationDialog,
  FormField,
  PopoverCloseButton,
  Trans,
  TypeaheadFormField,
  Typography,
} from 'components';
import { useHandleApiResult } from 'hooks/useHandleApiResult';
import { LocationChangeConfirmationContext } from 'providers/LocationChangeConfirmationProvider';

import { FieldError, useForm, useSelector, useTranslation } from 'third-party';

import { useCache } from 'modules/cache';
import { useContractorsList } from 'modules/contractorsList';

import { contractorsListSelectors } from 'selectors';

import { TypeaheadOption } from 'types/app';

const FormWrapper = styled.form`
  position: relative;
  width: 50rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;
  flex-grow: 1;
`;

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  flex-wrap: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
  padding: 2.2rem 2.3rem;
  z-index: 1;
  gap: 1.6rem;
`;

const Title = styled(Typography)`
  display: inline-block;
  line-height: 100%;
  color: ${props => props.theme.custom.palette.primary900};
  margin: 0;
  flex: 0 1 auto;
  white-space: nowrap;
  vertical-align: middle;
  padding-right: 1rem;
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1.6rem;
  flex-shrink: 0;
  flex-wrap: nowrap;
`;

const Content = styled(Stack)`
  flex-grow: 1;
  padding: 2.2rem 2.3rem 2.7rem;
`;

enum AddFormFields {
  Name = 'name',
  Address = 'address',
}

type AddForm = {
  [AddFormFields.Name]: string;
  [AddFormFields.Address]: TypeaheadOption | null;
};

const defaultValues: AddForm = {
  [AddFormFields.Name]: '',
  [AddFormFields.Address]: null,
};

type Props = {
  onClosePopup?: () => void;
  onSuccess?: (result: APICompany) => void;
};

export const AddContractorPopup: React.FC<Props> = ({
  onClosePopup,
  onSuccess,
}) => {
  const { t } = useTranslation();

  const { loadAddress } = useCache();

  const { createContractor } = useContractorsList();

  const isFormPending = useSelector(
    contractorsListSelectors.createContractorPending,
  );

  const {
    setValue,
    handleSubmit,
    control,
    formState: { isValid, errors, isDirty },
  } = useForm<AddForm>({
    defaultValues,
  });

  const {
    continueChangeLocation,
    addLocationChangeConfirmation,
    removeLocationChangeConfirmation,
  } = useContext(LocationChangeConfirmationContext);

  const [confirmNavigate, setConfirmNavigate] = useState(false);

  const onLocationChange = useCallback(() => {
    if (isDirty) {
      setConfirmNavigate(true);
      return false;
    }
  }, [isDirty]);

  useEffect(() => {
    return addLocationChangeConfirmation(onLocationChange);
  }, [addLocationChangeConfirmation, onLocationChange]);

  const loadAddressOptions = async (value: string) => {
    const result: APIResponse<string[]> = await loadAddress(value);
    let options: TypeaheadOption[] = [];
    if (!result.error) {
      options = result.payload.map(it => ({
        label: it,
        value: it,
      }));
    }
    return {
      options,
    };
  };

  const handleApiResult = useHandleApiResult();

  const onSubmit = async (data: AddForm) => {
    if (!isValid) {
      return;
    }
    handleApiResult<APICompany>(
      () =>
        createContractor({
          name: data[AddFormFields.Name].trim(),
          address: data[AddFormFields.Address]?.value || null,
          companyType: APIUserType.Contractor,
        }),
      ({ result, showBaseToast }) => {
        showBaseToast(
          <Trans
            i18nKey="contractors.addCustomerPopup.addSuccessMessage"
            shouldUnescape
          >
            "{{ companyName: result.payload.name }}" was successfully created.
          </Trans>,
        );
        removeLocationChangeConfirmation(onLocationChange);
        onClosePopup?.();
        onSuccess?.(result.payload);
      },
    );
  };

  const onSelectAddress = useCallback(
    (option: TypeaheadOption | null) => {
      setValue(AddFormFields.Address, option, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [setValue],
  );

  const handleConfirmNavigate = useCallback(() => {
    setConfirmNavigate(false);
    continueChangeLocation();
  }, [continueChangeLocation]);

  return (
    <>
      <ConfirmationDialog
        onClose={() => setConfirmNavigate(false)}
        open={confirmNavigate}
        handleActionClick={handleConfirmNavigate}
        description={t('common.exitConfirmationWindow.text')}
        cancelButtonTitle={t('common.continueEditing')}
        actionButtonTitle={t('common.confirmCancel')}
      />
      <PopoverCloseButton onClose={() => onClosePopup?.()} />
      <FormWrapper onSubmit={handleSubmit(onSubmit)}>
        <Header>
          <Title variant="h3">{t('customers.addCustomerPopup.title')}</Title>
          <ActionButtons>
            <Button
              disabled={isFormPending}
              variant="outlined"
              color="secondary"
              onClick={() => onClosePopup?.()}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              loading={isFormPending}
              disabled={isFormPending}
            >
              {t('customers.addCustomerButton')}
            </Button>
          </ActionButtons>
        </Header>
        <Content>
          <FormField
            fieldName={AddFormFields.Name}
            fieldError={errors[AddFormFields.Name] as FieldError}
            label={t('customers.addCustomerPopup.companyNameLabel')}
            placeholder={t('customers.addCustomerPopup.companyNamePlaceholder')}
            requiredErrorMessage={t('common.form.error.emptyField')}
            control={control}
            maxLength={textField.maxLength}
            isRequired
            isDisabled={isFormPending}
            inputProps={{
              maxLength: textField.maxLength,
            }}
          />
          <FormField
            fieldName={AddFormFields.Address}
            label={t('customers.addCustomerPopup.companyAddressLabel')}
            fieldError={errors[AddFormFields.Address] as FieldError}
            control={control}
            isRequired={false}
            render={() => (
              <TypeaheadFormField
                placeholder={t(
                  'customers.addCustomerPopup.companyAddressPlaceholder',
                )}
                loadOptions={loadAddressOptions}
                onChange={onSelectAddress}
                isDisabled={isFormPending}
              />
            )}
          />
        </Content>
      </FormWrapper>
    </>
  );
};
